import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiCheck, FiHeart, FiSliders, FiX } from "react-icons/fi";
import productData, { productCategories } from "../Datas/productData";
import { CartContext } from "./CartContext";
import { formatPrice } from "../config/siteConfig";
import "../css/shop.scss";

export function ProductCard({ product, priority = false }) {
  const { addToCart, isWishlisted, toggleWishlist } = useContext(CartContext);
  const [quickOpen, setQuickOpen] = useState(false);
  const [added, setAdded] = useState(false);
  const wished = isWishlisted(product.id);

  const quickAdd = (size) => {
    addToCart(product.id, 1, size, product.colors[0]);
    setAdded(true);
    window.setTimeout(() => {
      setAdded(false);
      setQuickOpen(false);
    }, 900);
  };

  return (
    <article className="product-card">
      <div className="product-card__media">
        <Link to={`/shopDetails/${product.id}`} aria-label={`View ${product.name}`}>
          <img
            src={product.image}
            alt={product.name}
            loading={priority ? "eager" : "lazy"}
            onError={(event) => {
              event.currentTarget.classList.add("image-error");
            }}
          />
        </Link>
        {product.tags.includes("new") && <span className="product-card__badge">New</span>}
        <button
          type="button"
          className={`product-card__heart ${wished ? "is-active" : ""}`}
          aria-label={`${wished ? "Remove" : "Add"} ${product.name} ${wished ? "from" : "to"} wishlist`}
          onClick={() => toggleWishlist(product.id)}
        >
          <FiHeart />
        </button>
        <button type="button" className="product-card__quick" onClick={() => setQuickOpen(true)}>
          Quick shop
        </button>
        {quickOpen && (
          <div className="quick-shop" role="group" aria-label={`Choose a size for ${product.name}`}>
            <button
              type="button"
              className="quick-shop__close"
              aria-label="Close quick shop"
              onClick={() => setQuickOpen(false)}
            >
              <FiX />
            </button>
            {added ? (
              <span className="quick-shop__added">
                <FiCheck /> Added to bag
              </span>
            ) : (
              <>
                <span>Select a size</span>
                <div>
                  {product.sizes.map((size) => (
                    <button type="button" key={size} onClick={() => quickAdd(size)}>
                      {size}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <div className="product-card__details">
        <Link to={`/shopDetails/${product.id}`}>
          <h3>{product.name}</h3>
        </Link>
        <span>{formatPrice(product.price)}</span>
        <p>{product.colors[0]}{product.colors.length > 1 ? ` · +${product.colors.length - 1} colors` : ""}</p>
      </div>
    </article>
  );
}

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const parameterKey = searchParams.toString();

  const query = (searchParams.get("q") || "").trim();
  const category = searchParams.get("category") || "All";
  const color = searchParams.get("color") || "";
  const size = searchParams.get("size") || "";
  const tag = searchParams.get("tag") || "";
  const sort = searchParams.get("sort") || "featured";

  const colors = useMemo(
    () => [...new Set(productData.flatMap((product) => product.colors))].sort(),
    []
  );
  const sizes = useMemo(
    () => [...new Set(productData.flatMap((product) => product.sizes))],
    []
  );

  useEffect(() => {
    setIsLoading(true);
    const timer = window.setTimeout(() => setIsLoading(false), 220);
    return () => window.clearTimeout(timer);
  }, [parameterKey]);

  const updateParameter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "All" || value === "featured") next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const visibleProducts = useMemo(() => {
    const normalizedQuery = query.toLowerCase();
    const result = productData.filter((product) => {
      const matchesQuery =
        !normalizedQuery ||
        [product.name, product.category, product.material, ...product.tags]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      const matchesCategory = category === "All" || product.category === category;
      const matchesColor = !color || product.colors.includes(color);
      const matchesSize = !size || product.sizes.includes(size);
      const matchesTag = !tag || product.tags.includes(tag.toLowerCase());
      return matchesQuery && matchesCategory && matchesColor && matchesSize && matchesTag;
    });

    return [...result].sort((a, b) => {
      if (sort === "price-low") return a.price - b.price;
      if (sort === "price-high") return b.price - a.price;
      if (sort === "name") return a.name.localeCompare(b.name);
      return a.id - b.id;
    });
  }, [category, color, query, size, sort, tag]);

  const resetFilters = () => setSearchParams({});
  const hasFilters = query || category !== "All" || color || size || tag || sort !== "featured";

  return (
    <main className="shop-page">
      <header className="shop-hero">
        <p className="eyebrow">THE COLLECTION</p>
        <h1>{query ? `Search: “${query}”` : category === "All" ? "New collection" : category}</h1>
        <p>
          Enduring menswear, precise cuts and expressive materials—pieces designed to live with you,
          season after season.
        </p>
      </header>

      <nav className="category-tabs" aria-label="Product categories">
        {productCategories.map((item) => (
          <button
            type="button"
            key={item}
            className={category === item ? "is-active" : ""}
            onClick={() => updateParameter("category", item)}
          >
            {item}
          </button>
        ))}
      </nav>

      <div className="shop-toolbar">
        <div>
          <button type="button" className="filter-toggle" onClick={() => setFiltersOpen((open) => !open)}>
            <FiSliders /> Filters
            {(color || size) && <span>{[color, size].filter(Boolean).length}</span>}
          </button>
          <span className="product-count">{visibleProducts.length} pieces</span>
        </div>
        <label>
          <span className="sr-only">Sort products</span>
          <select value={sort} onChange={(event) => updateParameter("sort", event.target.value)}>
            <option value="featured">Featured</option>
            <option value="price-low">Price: low to high</option>
            <option value="price-high">Price: high to low</option>
            <option value="name">Name</option>
          </select>
        </label>
      </div>

      {filtersOpen && (
        <section className="filter-panel" aria-label="Product filters">
          <div>
            <h2>Color</h2>
            <div className="filter-options filter-options--colors">
              {colors.map((item) => (
                <button
                  type="button"
                  key={item}
                  className={color === item ? "is-active" : ""}
                  onClick={() => updateParameter("color", color === item ? "" : item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          <div>
            <h2>Size</h2>
            <div className="filter-options filter-options--sizes">
              {sizes.map((item) => (
                <button
                  type="button"
                  key={item}
                  className={size === item ? "is-active" : ""}
                  onClick={() => updateParameter("size", size === item ? "" : item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          <button type="button" className="text-link filter-reset" onClick={resetFilters} disabled={!hasFilters}>
            Reset all
          </button>
        </section>
      )}

      {isLoading ? (
        <div className="product-grid product-grid--loading" aria-label="Loading products">
          {Array.from({ length: 8 }).map((_, index) => (
            <div className="product-skeleton" key={index} />
          ))}
        </div>
      ) : visibleProducts.length ? (
        <section className="product-grid" aria-live="polite">
          {visibleProducts.map((product, index) => (
            <ProductCard key={product.id} product={product} priority={index < 4} />
          ))}
        </section>
      ) : (
        <section className="shop-empty">
          <h2>No pieces found</h2>
          <p>Try another search or remove one of your filters.</p>
          <button type="button" className="button button--dark" onClick={resetFilters}>
            View the full collection
          </button>
        </section>
      )}
    </main>
  );
}

export default Shop;
